import { requestTokenRefresh } from '../../utils/token-refresh.js';

const warningLeadMs = 60 * 1000;
const proactiveRefreshRatio = 0.8;
const MAX_TIMEOUT_MS = 2147483647;

let warningTimeoutId = null;
let expireTimeoutId = null;
let countdownIntervalId = null;
let proactiveRefreshTimeoutId = null;

const clearPopupTimers = () => {
    if (warningTimeoutId) {
        clearTimeout(warningTimeoutId);
        warningTimeoutId = null;
    }

    if (expireTimeoutId) {
        clearTimeout(expireTimeoutId);
        expireTimeoutId = null;
    }

    if (countdownIntervalId) {
        clearInterval(countdownIntervalId);
        countdownIntervalId = null;
    }
};

const clearProactiveRefreshTimer = () => {
    if (proactiveRefreshTimeoutId) {
        clearTimeout(proactiveRefreshTimeoutId);
        proactiveRefreshTimeoutId = null;
    }
};

const getJwtPayload = (token) => {
    if (!token || typeof token !== 'string') {
        return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
        return null;
    }

    try {
        return JSON.parse(atob(parts[1].replaceAll('-', '+').replaceAll('_', '/')));
    } catch {
        return null;
    }
};

const getJwtExpMs = (token) => {
    const payload = getJwtPayload(token);
    if (!payload?.exp || typeof payload.exp !== 'number') {
        return null;
    }
    return payload.exp * 1000;
};

const getProactiveRefreshDelayMs = (token) => {
    const payload = getJwtPayload(token);
    const iat = payload?.iat;
    const exp = payload?.exp;

    if (typeof iat !== 'number' || typeof exp !== 'number') {
        return null;
    }

    const iatMs = iat * 1000;
    const expMs = exp * 1000;
    const ttlMs = expMs - iatMs;

    if (ttlMs <= 0) {
        return 0;
    }

    const refreshAtMs = iatMs + Math.floor(ttlMs * proactiveRefreshRatio);
    return Math.max(0, refreshAtMs - Date.now());
};

const toEpochMs = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return null;
    }

    // Some backends return TTL (seconds from now) instead of absolute epoch.
    // Treat small values as relative seconds to avoid immediate expiry.
    if (parsed < 1e9) {
        return Date.now() + parsed * 1000;
    }

    // 10-digit unix timestamps are seconds, 13-digit are milliseconds.
    return parsed < 1e12 ? parsed * 1000 : parsed;
};

const state = () => ({
    accessToken: null,
    accessTokenExpiresAt: null,
    showExpiryWarning: false,
    showExpired: false,
    warningSecondsLeft: null,
});

const mutations = {
    SET_ACCESS_TOKEN(state, accessToken) {
        state.accessToken = accessToken;
    },
    SET_ACCESS_TOKEN_EXPIRES_AT(state, accessTokenExpiresAt) {
        state.accessTokenExpiresAt = accessTokenExpiresAt;
    },
    SET_WARNING_VISIBLE(state, value) {
        state.showExpiryWarning = value;
    },
    SET_EXPIRED_VISIBLE(state, value) {
        state.showExpired = value;
    },
    SET_WARNING_SECONDS_LEFT(state, value) {
        state.warningSecondsLeft = value;
    },
};

const actions = {
    setAccessToken({ commit, dispatch }, { token, expiresAtSeconds, expiresAt, refreshTokenExpiresAt }) {
        commit('SET_ACCESS_TOKEN', token);

        const resolvedExpiresAt =
            toEpochMs(refreshTokenExpiresAt) ??
            toEpochMs(expiresAt) ??
            toEpochMs(expiresAtSeconds);
        const fallbackExpiresAt = getJwtExpMs(token);

        commit('SET_ACCESS_TOKEN_EXPIRES_AT', resolvedExpiresAt ?? fallbackExpiresAt);
        commit('SET_WARNING_VISIBLE', false);
        commit('SET_EXPIRED_VISIBLE', false);

        dispatch('scheduleTimers');
        dispatch('scheduleProactiveRefresh');
    },

    clearSession({ commit }) {
        clearPopupTimers();
        clearProactiveRefreshTimer();
        commit('SET_ACCESS_TOKEN', null);
        commit('SET_ACCESS_TOKEN_EXPIRES_AT', null);
        commit('SET_WARNING_VISIBLE', false);
        commit('SET_EXPIRED_VISIBLE', false);
        commit('SET_WARNING_SECONDS_LEFT', null);

        if (typeof window !== 'undefined') {
            window.localStorage.removeItem('vuexState');
        }
    },

    initSession({ state, commit, dispatch }) {
        if (!state.accessToken) {
            return;
        }

        if (!state.accessTokenExpiresAt) {
            return;
        }

        const msLeft = state.accessTokenExpiresAt - Date.now();
        if (msLeft <= 0) {
            commit('SET_EXPIRED_VISIBLE', true);
            commit('SET_WARNING_VISIBLE', false);
            commit('SET_WARNING_SECONDS_LEFT', 0);
            return;
        }

        dispatch('scheduleTimers');
        dispatch('scheduleProactiveRefresh');
    },

    scheduleTimers({ state, commit, dispatch }) {
        clearPopupTimers();

        if (!state.accessTokenExpiresAt) {
            return;
        }

        const msLeft = state.accessTokenExpiresAt - Date.now();
        if (msLeft <= 0) {
            commit('SET_EXPIRED_VISIBLE', true);
            commit('SET_WARNING_VISIBLE', false);
            commit('SET_WARNING_SECONDS_LEFT', 0);
            return;
        }

        // Browser timers overflow above ~24.8 days (2^31-1 ms). Reschedule safely.
        if (msLeft > MAX_TIMEOUT_MS) {
            expireTimeoutId = setTimeout(() => {
                dispatch('scheduleTimers');
            }, MAX_TIMEOUT_MS);
            return;
        }

        const warningInMs = Math.max(0, msLeft - warningLeadMs);

        if (warningInMs === 0) {
            commit('SET_WARNING_VISIBLE', true);
            dispatch('startCountdown');
        } else {
            warningTimeoutId = setTimeout(() => {
                commit('SET_WARNING_VISIBLE', true);
                dispatch('startCountdown');
            }, Math.min(warningInMs, MAX_TIMEOUT_MS));
        }

        expireTimeoutId = setTimeout(() => {
            commit('SET_EXPIRED_VISIBLE', true);
            commit('SET_WARNING_VISIBLE', false);
            dispatch('stopCountdown');
        }, Math.min(msLeft, MAX_TIMEOUT_MS));
    },

    scheduleProactiveRefresh({ state, dispatch }) {
        clearProactiveRefreshTimer();

        const delayMs = getProactiveRefreshDelayMs(state.accessToken);
        if (delayMs === null) {
            return;
        }

        proactiveRefreshTimeoutId = setTimeout(() => {
            dispatch('refreshAccessTokenProactively');
        }, delayMs);
    },

    async refreshAccessTokenProactively({ state, dispatch }) {
        if (!state.accessToken) {
            return;
        }

        try {
            // Shares the in-flight refresh with the request-level 401 fallback, so the
            // two never consume the same single-use refresh cookie at the same time.
            const { token, expiresAt } = await requestTokenRefresh();

            await dispatch('setAccessToken', { token, expiresAt });
        } catch {
            // Keep silent: request-level 401 fallback remains the safety net.
        }
    },

    startCountdown({ state, commit }) {
        const updateSecondsLeft = () => {
            const msLeft = state.accessTokenExpiresAt - Date.now();
            const secondsLeft = Math.max(0, Math.floor(msLeft / 1000));
            commit('SET_WARNING_SECONDS_LEFT', secondsLeft);
        };

        updateSecondsLeft();
        if (!countdownIntervalId) {
            countdownIntervalId = setInterval(updateSecondsLeft, 1000);
        }
    },

    stopCountdown({ commit }) {
        if (countdownIntervalId) {
            clearInterval(countdownIntervalId);
            countdownIntervalId = null;
        }
        commit('SET_WARNING_SECONDS_LEFT', null);
    },
};

const getters = {
    accessToken: (state) => state.accessToken,
    accessTokenExpiresAt: (state) => state.accessTokenExpiresAt,
    showExpiryWarning: (state) => state.showExpiryWarning,
    showExpired: (state) => state.showExpired,
    warningSecondsLeft: (state) => state.warningSecondsLeft,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
